import React, { useState } from "react";
import { Row, Col, Label, Input, Button } from "reactstrap";
import api from "../../../api";

// Go-live parity (2026-09-24): DMS#22 — the legacy ECN "ECO notes" panel
// (DMSNew.vb:386-491, :777-781; saved by DmsDB.UpdateEco, DmsDB.vb:125-161):
// ECO notes plus "customer approval received" / "customer notification
// received". Everyone who opens the ECN sees them; only Document Control edits
// them (legacy: the release administrator). Saved through PUT /dms/{id}/eco-notes.
const EcoNotesPanel = ({ dmsId, ecn, canEdit, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ ecnNotes: "", customerApprovalReceived: false, customerNotificationReceived: false });

    const startEdit = () => {
        setForm({
            ecnNotes: ecn?.ecnNotes ?? "",
            customerApprovalReceived: Boolean(ecn?.customerApprovalReceived),
            customerNotificationReceived: Boolean(ecn?.customerNotificationReceived),
        });
        setError(null);
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await api.put(`/dms/${dmsId}/eco-notes`, form);
            if (onSaved) onSaved(res.data || form);
            setEditing(false);
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || "Failed to save the ECO notes.");
        } finally {
            setSaving(false);
        }
    };

    const yesNo = (v) => (v ? <span className="text-success">✓ Yes</span> : <span className="text-muted">—</span>);

    return (
        <div className="border rounded p-3 mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-semibold mb-0">ECO notes</h6>
                {canEdit && !editing && (
                    <Button type="button" color="primary" outline size="sm" onClick={startEdit}>
                        <i className="ri-edit-line align-bottom me-1"></i>Edit
                    </Button>
                )}
            </div>
            {editing ? (
                <Row className="g-3">
                    <Col md={6}>
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input" type="checkbox" id="ecoApprovalReceived"
                                checked={form.customerApprovalReceived} disabled={saving}
                                onChange={(e) => setForm((p) => ({ ...p, customerApprovalReceived: e.target.checked }))}
                            />
                            <label className="form-check-label" htmlFor="ecoApprovalReceived">Customer approval received</label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input" type="checkbox" id="ecoNotificationReceived"
                                checked={form.customerNotificationReceived} disabled={saving}
                                onChange={(e) => setForm((p) => ({ ...p, customerNotificationReceived: e.target.checked }))}
                            />
                            <label className="form-check-label" htmlFor="ecoNotificationReceived">Customer notification received</label>
                        </div>
                    </Col>
                    <Col md={6}>
                        <Label for="ecoNotes" className="small fw-semibold mb-1">ECO notes</Label>
                        <Input
                            type="textarea" id="ecoNotes" rows={4} maxLength={20000}
                            value={form.ecnNotes} disabled={saving}
                            onChange={(e) => setForm((p) => ({ ...p, ecnNotes: e.target.value }))}
                        />
                    </Col>
                    {error && (
                        <Col xs={12}>
                            <div className="alert alert-danger mb-0 py-2 small">{error}</div>
                        </Col>
                    )}
                    <Col xs={12} className="d-flex justify-content-end gap-2">
                        <Button type="button" color="light" size="sm" disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
                        <Button type="button" color="primary" size="sm" disabled={saving} onClick={save}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </Col>
                </Row>
            ) : (
                <Row className="g-3">
                    <Col md={6}>
                        <div className="small"><span className="text-muted me-2">Customer approval received:</span>{yesNo(ecn?.customerApprovalReceived)}</div>
                        <div className="small mt-1"><span className="text-muted me-2">Customer notification received:</span>{yesNo(ecn?.customerNotificationReceived)}</div>
                    </Col>
                    <Col md={6}>
                        <div className="small" style={{ whiteSpace: "pre-wrap" }}>
                            {ecn?.ecnNotes || <span className="text-muted">No ECO notes.</span>}
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default EcoNotesPanel;
